import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function VendorApply() {
  const [cats, setCats] = useState([]);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    business_name: "", category_slug: "", subcategory_slug: "", city: "", locality: "",
    services: "", description: "", website: "", contact_name: "", mobile: "", email: "", consent: false,
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { api.get("/discovery/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const subs = cats.find((c) => c.slug === f.category_slug)?.subcategories || [];

  const submit = async () => {
    if (!f.business_name || !f.category_slug || !f.city || !f.contact_name || !f.mobile || !f.email)
      return toast.error("Please fill business, category, city and contact details");
    if (!f.consent) return toast.error("Please accept the listing terms");
    setSubmitting(true);
    try {
      const res = await api.post("/discovery/vendor-apply", {
        ...f, services: f.services ? f.services.split(",").map((s) => s.trim()).filter(Boolean) : [],
        subcategory_slug: f.subcategory_slug || null, website: f.website || null,
      });
      setDone(res.data); toast.success("Application submitted");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setSubmitting(false);
  };

  if (done) return (
    <div className="ca-container py-16 max-w-lg text-center" data-testid="vendor-apply-success">
      <ShieldCheck className="h-12 w-12 text-emerald-600 mx-auto" />
      <h1 className="font-display text-2xl font-bold text-slate-900 mt-3">Listing submitted</h1>
      <p className="text-slate-600 mt-2">Application ID: <b className="font-mono">{done.application_id}</b></p>
      <p className="text-sm text-slate-500 mt-1">{done.message}</p>
    </div>
  );

  return (
    <div className="ca-container py-10 max-w-2xl">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-brand/10 text-teal px-3 py-1 text-xs font-semibold mb-3"><Store className="h-3.5 w-3.5" /> For businesses</span>
      <h1 className="font-display text-3xl font-bold text-slate-900">List Your Business</h1>
      <p className="text-slate-600 mt-2">Add your business to the ComplaintAdvisor directory. Listings are reviewed before they appear publicly. We do not make claims like “best” or “guaranteed”.</p>
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label>Business name *</Label><Input value={f.business_name} onChange={set("business_name")} data-testid="apply-business" /></div>
        <div>
          <Label>Category *</Label>
          <Select value={f.category_slug} onValueChange={(v) => setF({ ...f, category_slug: v, subcategory_slug: "" })}>
            <SelectTrigger data-testid="apply-category"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="max-h-64">{cats.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Subcategory</Label>
          <Select value={f.subcategory_slug} onValueChange={(v) => setF({ ...f, subcategory_slug: v })} disabled={!subs.length}>
            <SelectTrigger data-testid="apply-subcategory"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="max-h-64">{subs.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>City *</Label><Input value={f.city} onChange={set("city")} data-testid="apply-city" /></div>
        <div><Label>Locality</Label><Input value={f.locality} onChange={set("locality")} data-testid="apply-locality" /></div>
        <div className="sm:col-span-2"><Label>Services (comma separated)</Label><Input value={f.services} onChange={set("services")} placeholder="e.g. Leak Repair, Pipeline Installation" data-testid="apply-services" /></div>
        <div className="sm:col-span-2"><Label>Description</Label><Textarea rows={3} value={f.description} onChange={set("description")} data-testid="apply-description" /></div>
        <div><Label>Website</Label><Input value={f.website} onChange={set("website")} placeholder="https://…" data-testid="apply-website" /></div>
        <div><Label>Contact name *</Label><Input value={f.contact_name} onChange={set("contact_name")} data-testid="apply-contact" /></div>
        <div><Label>Mobile *</Label><Input value={f.mobile} onChange={set("mobile")} placeholder="+91…" data-testid="apply-mobile" /></div>
        <div><Label>Email *</Label><Input value={f.email} onChange={set("email")} data-testid="apply-email" /></div>
        <label className="sm:col-span-2 flex items-start gap-2 text-xs text-slate-600">
          <Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: v })} data-testid="apply-consent" className="mt-0.5" />
          I confirm the information is accurate and agree to ComplaintAdvisor's listing terms. Contact details are shared only with my consent.
        </label>
        <div className="sm:col-span-2"><Button className="bg-teal-brand hover:bg-teal-700 text-white w-full" disabled={submitting} onClick={submit} data-testid="apply-submit">{submitting ? "Submitting…" : "Submit listing for review"}</Button></div>
      </div>
    </div>
  );
}
